import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  giftChoiceUnitListDataAtom,
  giftChoiceUnitListStatusAtom,
} from '@shared/api/giftChoiceUnit/giftChoiceUnit.atom';
import {
  deleteGiftChoiceUnit,
  updateGiftChoiceUnit,
} from '@shared/api/giftChoiceUnit/giftChoiceUnit.controller';
import { useGiftChoiceUnitListHook } from '@shared/api/giftChoiceUnit/giftChoiceUnit.hook';
import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { api_ } from '@shared/plugIn/axios';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { Link } from 'react-router-dom';
import { useAsyncFn } from 'react-use';

import { useSessionPersistedState } from '../hooks/useSessionPersistedState';
import {
  GiftItemInfoUsageLite,
  buildUnitUsageMap,
  usageChipPrice,
  usageChipTitle,
} from '../utils/choiceUnitUsage.utils';

//* 한 줄에 펼쳐 보여줄 상품 칩 개수 (나머지는 +N, 툴팁에 전체)
const USAGE_CHIP_LIMIT = 3;

/**
 * 유닛별 "어떤 상품에 걸려 있는지" 조회 — 상품 목록을 필요한 필드만 한 번 읽어 맵으로 뒤집는다.
 * 실패(라우트 미개방 404 등)하면 null — 이 정보만 숨기고 목록은 그대로 동작한다.
 */
const fetchUnitUsageMap = async (
  role: LowerRole,
): Promise<Map<string, GiftItemInfoUsageLite[]> | null> => {
  try {
    const { data } = await api_.get<GiftItemInfoUsageLite[]>(
      `/${role}/gift-item-info?select=${encodeURIComponent(
        'name isEnabled choiceUnits priceRetail priceRegular priceWhole',
      )}`,
    );
    return buildUnitUsageMap(data);
  } catch {
    return null;
  }
};

/**
 * 정가 대비 도매가 비율 — 원가율(도매가 / 정가 × 100, 단가표 PriceTableView와 동일 정의)과
 * 정가 대비 차이(원가율 − 100, 도매가 쪽이 싸면 음수)를 함께 돌려준다.
 * 정가가 0 이하이거나 도매가가 없으면 계산 불가(null).
 */
const calcCostRate = (
  priceWhole?: number,
  priceRegular?: number,
): { costRate: number; gapRate: number } | null => {
  if (!priceWhole || !priceRegular || priceRegular <= 0) return null;
  const costRate = (priceWhole / priceRegular) * 100;
  return { costRate, gapRate: costRate - 100 };
};

export const GiftChoiceUnitListPage: FC = function GiftChoiceUnitListPage() {
  const { sellerRole } = useAuth();

  useGiftChoiceUnitListHook(sellerRole());
  useGiftItemBrandListHook(sellerRole());

  const status = useRecoilValue(giftChoiceUnitListStatusAtom);
  const data = useRecoilValue(giftChoiceUnitListDataAtom);
  const setData = useSetRecoilState(giftChoiceUnitListDataAtom);

  const brandListStatus = useRecoilValue(giftItemBrandListStatusAtom);
  const brandList = useRecoilValue(giftItemBrandListDataAtom);

  //* 유닛별 "판매중인 상품" 표기용 (실패해도 목록은 정상)
  //* deps는 반드시 role '값' — sellerRole 함수는 매 렌더 새 참조라 넣으면 무한 조회가 된다.
  const role = sellerRole();
  const [{ value: unitUsageMap }, loadUnitUsage] = useAsyncFn(
    () => fetchUnitUsageMap(role),
    [role],
  );
  useEffect(() => {
    loadUnitUsage();
  }, [loadUnitUsage]);

  //* 필터는 sessionStorage에 유지 — 수정/등록 페이지를 다녀오거나 새로고침해도 그대로 남는다.
  const [searchQuery, setSearchQuery] = useSessionPersistedState(
    'giftChoiceUnitList.q',
    '',
  );
  const [brandFilter, setBrandFilter] = useSessionPersistedState<string>(
    'giftChoiceUnitList.brand',
    'all',
  );
  const [priceMin, setPriceMin] = useSessionPersistedState(
    'giftChoiceUnitList.priceMin',
    '',
  );
  const [priceMax, setPriceMax] = useSessionPersistedState(
    'giftChoiceUnitList.priceMax',
    '',
  );
  const [enabledFilter, setEnabledFilter] = useSessionPersistedState<string>(
    'giftChoiceUnitList.enabled',
    'all',
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  //* 필터가 하나라도 걸려 있는지 (초기화 버튼·결과 수 표기용)
  const hasActiveFilter =
    !!searchQuery ||
    brandFilter !== 'all' ||
    !!priceMin ||
    !!priceMax ||
    enabledFilter !== 'all';

  //* 필터 초기화 — 세션 저장분도 기본값으로 덮어써 다음 방문에 되살아나지 않는다.
  const resetAllFilters = useCallback(() => {
    setSearchQuery('');
    setBrandFilter('all');
    setPriceMin('');
    setPriceMax('');
    setEnabledFilter('all');
  }, [setSearchQuery, setBrandFilter, setPriceMin, setPriceMax, setEnabledFilter]);

  // 필터링된 데이터
  const filteredData = useMemo(() => {
    if (!data) return [];

    let result = [...data];

    // 검색 필터
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query),
      );
    }

    // 브랜드 필터
    if (brandFilter !== 'all') {
      if (brandFilter === 'none') {
        result = result.filter((item) => !item.giftItemBrand);
      } else {
        result = result.filter((item) => {
          const brandId =
            typeof item.giftItemBrand === 'object'
              ? item.giftItemBrand?._id
              : item.giftItemBrand;
          return brandId === brandFilter;
        });
      }
    }

    // 가격 필터
    const min = priceMin ? Number(priceMin) : null;
    const max = priceMax ? Number(priceMax) : null;
    if (min !== null || max !== null) {
      result = result.filter((item) => {
        const price = item.priceRegular ?? 0;
        if (min !== null && price < min) return false;
        if (max !== null && price > max) return false;
        return true;
      });
    }

    // 판매상태 필터
    if (enabledFilter !== 'all') {
      const isEnabled = enabledFilter === 'enabled';
      result = result.filter((item) => (item.enabled !== false) === isEnabled);
    }

    return result;
  }, [data, searchQuery, brandFilter, priceMin, priceMax, enabledFilter]);

  // 판매상태 토글 핸들러
  const handleToggleEnabled = useCallback(
    async (unit: GiftChoiceUnitData) => {
      const nextEnabled = unit.enabled === false;
      try {
        setTogglingId(unit._id);
        const updated = await updateGiftChoiceUnit(
          unit._id,
          { enabled: nextEnabled },
          sellerRole(),
        );
        setData((prev) => prev.map((item) => (item._id === unit._id ? updated : item)));
      } catch (error) {
        console.error(error);
        alert('상태 변경에 실패했습니다.');
      } finally {
        setTogglingId(null);
      }
    },
    [sellerRole, setData],
  );

  // 삭제 핸들러
  const handleDelete = useCallback(
    async (unit: GiftChoiceUnitData) => {
      if (!confirm(`"${unit.name}" 선택유닛을 삭제하시겠습니까?`)) return;

      try {
        setDeletingId(unit._id);
        await deleteGiftChoiceUnit(unit._id, sellerRole());
        setData((prev) => prev.filter((item) => item._id !== unit._id));
        alert('삭제되었습니다.');
      } catch (error) {
        console.error(error);
        alert('삭제에 실패했습니다.');
      } finally {
        setDeletingId(null);
      }
    },
    [sellerRole, setData],
  );

  // 브랜드별 그룹 + 가격 오름차순 정렬
  const groupedData = useMemo(() => {
    const groups: { brandId: string; brandName: string; items: GiftChoiceUnitData[] }[] =
      [];
    const brandMap: Record<string, GiftChoiceUnitData[]> = {};
    const noBrandItems: GiftChoiceUnitData[] = [];

    for (const item of filteredData) {
      if (item.giftItemBrand && typeof item.giftItemBrand === 'object') {
        const id = item.giftItemBrand._id;
        if (!brandMap[id]) brandMap[id] = [];
        brandMap[id]?.push(item);
      } else {
        noBrandItems.push(item);
      }
    }

    const sortByPrice = (a: GiftChoiceUnitData, b: GiftChoiceUnitData) =>
      (a.priceRegular ?? 0) - (b.priceRegular ?? 0);

    for (const brandId of Object.keys(brandMap)) {
      const items = brandMap[brandId] ?? [];
      if (items.length === 0) continue;
      const firstItem = items[0];
      if (!firstItem) continue;
      const brand = firstItem.giftItemBrand;
      const brandName = typeof brand === 'object' ? brand.name : brandId;

      groups.push({ brandId, brandName, items: items.sort(sortByPrice) });
    }

    groups.sort((a, b) => a.brandName?.localeCompare(b.brandName));

    if (noBrandItems.length > 0) {
      groups.push({
        brandId: 'none',
        brandName: '브랜드 없음',
        items: noBrandItems.sort(sortByPrice),
      });
    }

    return groups;
  }, [filteredData]);

  const renderItem = useCallback(
    (item: GiftChoiceUnitData) => (
      <div
        key={item._id}
        className={`flex items-center justify-between rounded-lg border p-3 ${
          item.enabled === false
            ? 'border-gray-200 bg-gray-50 opacity-60'
            : 'bg-white hover:bg-gray-50'
        }`}
      >
        <div className="flex flex-1 items-center gap-3">
          <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
            {item.image && typeof item.image === 'object' ? (
              <img
                src={fileBucketUrl(item.image.url, 'raw')}
                alt={item.name}
                className={`h-full w-full object-cover ${
                  item.enabled === false ? 'opacity-60 grayscale' : ''
                }`}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                No
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p
              className={`font-medium ${
                item.enabled === false ? 'text-gray-400 line-through' : ''
              }`}
            >
              {item.name}
            </p>
            {/* 이 유닛을 선택지로 쓰는 상품 — 판매중은 파랑, 판매종료는 회색 */}
            {unitUsageMap ? (
              <div className="mt-0.5 flex flex-wrap items-center gap-1">
                {(() => {
                  const infos = unitUsageMap.get(item._id) ?? [];
                  if (infos.length === 0) {
                    return (
                      <span className="text-xs text-amber-600">사용 중인 상품 없음</span>
                    );
                  }
                  const shown = infos.slice(0, USAGE_CHIP_LIMIT);
                  const allNames = infos
                    .map((info) => {
                      const price = usageChipPrice(info);
                      return `${info.name}${price ? ` ${price.toLocaleString()}원` : ''}${
                        info.isEnabled === false ? ' (종료)' : ''
                      }`;
                    })
                    .join(', ');
                  return (
                    <>
                      {shown.map((info) => {
                        const price = usageChipPrice(info);
                        return (
                          <Link
                            key={info._id}
                            to={`/gift-seller/item-info/${info._id}/edit`}
                            title={usageChipTitle(info)}
                            className={`flex max-w-[16rem] items-center gap-1 rounded px-1.5 py-0.5 text-xs ${
                              info.isEnabled === false
                                ? 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                            }`}
                          >
                            <span className="truncate">{info.name}</span>
                            {price ? (
                              <span className="flex-shrink-0 font-bold">
                                {price.toLocaleString()}원
                              </span>
                            ) : null}
                          </Link>
                        );
                      })}
                      {infos.length > shown.length ? (
                        <span className="text-xs text-gray-400" title={allNames}>
                          +{infos.length - shown.length}
                        </span>
                      ) : null}
                    </>
                  );
                })()}
              </div>
            ) : null}
          </div>
          {(item.priceRegular || item.priceWhole) && (
            <div className="flex items-center gap-1 text-sm">
              {item.priceRegular ? (
                <span className="font-medium text-gray-700">
                  {item.priceRegular.toLocaleString()}원
                </span>
              ) : null}
              {item.priceWhole ? (
                <span className="text-xs text-gray-400">
                  (도매 {item.priceWhole.toLocaleString()}원)
                </span>
              ) : null}
              {/* 정가 대비 도매가 비율 — 원가율 + 정가 대비 차이(-12.0%) */}
              {(() => {
                const rate = calcCostRate(item.priceWhole, item.priceRegular);
                if (!rate) return null;
                return (
                  <span className="whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 text-xs font-bold text-gray-800">
                    {rate.costRate.toFixed(1)}%
                    <span
                      className={`ml-1 ${
                        rate.gapRate <= 0 ? 'text-blue-600' : 'text-rose-600'
                      }`}
                    >
                      {rate.gapRate > 0 ? '+' : ''}
                      {rate.gapRate.toFixed(1)}%
                    </span>
                  </span>
                );
              })()}
            </div>
          )}
        </div>
        <div className="ml-3 flex items-center gap-2">
          <button
            onClick={() => handleToggleEnabled(item)}
            disabled={togglingId === item._id}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out disabled:opacity-50 ${
              item.enabled !== false ? 'bg-green-500' : 'bg-gray-300'
            }`}
            title={
              item.enabled !== false
                ? '판매중 (클릭하여 중단)'
                : '판매중단 (클릭하여 활성화)'
            }
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                item.enabled !== false ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
          <Link
            to={`/gift-seller/choice-unit/${item._id}/edit`}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
          >
            수정
          </Link>
          <button
            onClick={() => handleDelete(item)}
            disabled={deletingId === item._id}
            className="rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            {deletingId === item._id ? '삭제 중...' : '삭제'}
          </button>
        </div>
      </div>
    ),
    [togglingId, deletingId, handleDelete, handleToggleEnabled, unitUsageMap],
  );

  const itemListRender = useMemo(() => {
    if (status === 'success') {
      if (filteredData.length === 0) {
        return (
          <div className="py-8 text-center text-gray-500">
            {hasActiveFilter ? '검색 결과가 없습니다.' : '등록된 선택유닛이 없습니다.'}
          </div>
        );
      }

      return (
        <div className="space-y-4">
          {groupedData.map((group) => (
            <div key={group.brandId}>
              <div className="mb-2 flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-700">{group.brandName}</h3>
                <span className="text-xs text-gray-400">{group.items.length}개</span>
              </div>
              <div className="space-y-1.5">{group.items.map(renderItem)}</div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="center-box h-screen-15">
        <Loading />
      </div>
    );
  }, [status, filteredData, groupedData, hasActiveFilter, renderItem]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">선택유닛 관리</h1>
          <Link
            to={`/gift-seller/choice-unit/new${
              brandFilter !== 'all' && brandFilter !== 'none'
                ? `?brandId=${brandFilter}`
                : ''
            }`}
          >
            <Button className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white">
              + 선택유닛 등록
            </Button>
          </Link>
        </div>

        {/* 통계 */}
        {status === 'success' && (
          <div className="mt-3 text-sm text-gray-600">
            전체 <span className="font-medium text-gray-900">{data?.length || 0}</span>개
          </div>
        )}
      </div>

      {/* 필터 및 검색 */}
      <div className="border-b bg-white px-6 py-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* 검색 */}
          <div className="relative flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="유닛명, 설명 검색..."
              className="w-full rounded-lg border px-3 py-2 pl-9 text-sm"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              🔍
            </span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {/* 가격 필터 */}
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
              placeholder="최저가"
              min={0}
              className="w-24 rounded-lg border px-2 py-2 text-sm"
            />
            <span className="text-gray-400">~</span>
            <input
              type="number"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              placeholder="최대가"
              min={0}
              className="w-24 rounded-lg border px-2 py-2 text-sm"
            />
            <span className="text-xs text-gray-400">원</span>
            {(priceMin || priceMax) && (
              <button
                onClick={() => {
                  setPriceMin('');
                  setPriceMax('');
                }}
                className="rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
              >
                ✕
              </button>
            )}
          </div>

          {/* 브랜드 필터 */}
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
            disabled={brandListStatus !== 'success'}
          >
            <option value="all">전체 브랜드</option>
            <option value="none">브랜드 없음</option>
            {brandList.map((brand) => (
              <option key={brand._id} value={brand._id}>
                {brand.name}
              </option>
            ))}
          </select>

          {/* 판매상태 필터 */}
          <select
            value={enabledFilter}
            onChange={(e) => setEnabledFilter(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 상태</option>
            <option value="enabled">판매중</option>
            <option value="disabled">판매중단</option>
          </select>

          {/* 필터 초기화 */}
          {hasActiveFilter && (
            <button
              onClick={resetAllFilters}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
            >
              필터 초기화
            </button>
          )}
        </div>

        {/* 검색 결과 수 */}
        {status === 'success' && hasActiveFilter && (
          <div className="mt-2 text-xs text-gray-500">
            {filteredData.length}개 표시 (전체 {data?.length || 0}개 중)
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">{itemListRender}</div>
    </div>
  );
};
